import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import humanizeUtil from '@src/utils/humanizeUtil';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import tracker from '@src/utils/sensor/apptracker';
import {
  Row,
  Cell,
  FullCell,
  PercentBadge,
  SuccessCellWrapper,
  SuccessTrendWrapper,
  TimeoutCaption,
  DodNeutral,
  GaugeContainer,
  GaugeWrapper,
} from '../styled';
import type { ColumnWidths } from '../types';
import { get } from 'lodash';
import { appliedFilterAtom, appliedIntervalAtom, enableDodAtom } from '../../../atoms';
import {
  generateFlowPresetUrl,
  generateFlowPresetUrlWithStorage,
  openFlowPreset,
} from '../../../drillDown';
import { useNavigate } from 'react-router-dom';
import { shouldUseBrowserDefaultNavigation } from '../../../utils';
import { unitMapping } from '../../utils';
import Trend from './Charts/Trend';
import Gauge from './Charts/Gauge';
import useFetchDodFlowData from '../../hooks/useFetchDodFlowData';
import { RiseOutlined, FallOutlined, LoadingOutlined } from '@ant-design/icons';

interface RowDataProps {
  flowMeta: any;
  data: any;
  columnWidths: ColumnWidths;
}

const RowData: React.FC<RowDataProps> = ({ flowMeta, data, columnWidths }) => {
  const { nameColumnWidth, initColumnWidth, successColumnWidth } = columnWidths;
  const appliedFilter = useAtomValue(appliedFilterAtom);
  const appliedInterval = useAtomValue(appliedIntervalAtom);
  const enableDod = useAtomValue(enableDodAtom);

  const flowMetric = flowMeta.metrics || {};
  const flowSettings = flowMeta.definition?.settings || {};
  const successPctMetricKey = getPercentageMetricByKey({ metricKey: flowMetric.success });

  const dodQuery = useFetchDodFlowData({
    flowId: flowMeta.id,
    flowMeta,
    enabled: enableDod,
  });

  const initData = get(data, ['totals', flowMetric.init], null);
  const successPctData = get(data, ['totals', successPctMetricKey], 0);

  const dodSuccessPctData = enableDod
    ? get(dodQuery.data, ['totals', successPctMetricKey], null)
    : null;

  const successPctChange = useMemo(() => {
    if (
      !enableDod ||
      successPctData === null ||
      dodSuccessPctData === null ||
      dodSuccessPctData === 0
    ) {
      return null;
    }
    return ((successPctData - dodSuccessPctData) / dodSuccessPctData) * 100;
  }, [enableDod, successPctData, dodSuccessPctData]);

  const dodSuccessPctTimeseries = enableDod
    ? get(dodQuery.data, ['data', successPctMetricKey], [])
    : [];

  const initHumanized = humanizeUtil(initData, flowMetric.init);

  const navigate = useNavigate();

  const flowUrl = generateFlowPresetUrl({ flowMeta }) || '#';

  const onFlowClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (shouldUseBrowserDefaultNavigation(e)) {
      return;
    }
    e.preventDefault();
    tracker('Click preset link from Overview Table Flow', {
      flowId: flowMeta.id,
    });
    openFlowPreset({ flowMeta, appliedFilter, appliedInterval, navigate });
  };

  const onFlowContextMenu = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = generateFlowPresetUrlWithStorage({
      flowMeta,
      appliedFilter,
      appliedInterval,
    });
    if (url) {
      tracker('Right-click preset link from Overview Table Flow', {
        flowId: flowMeta.id,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Row href={flowUrl} onClick={onFlowClick} onContextMenu={onFlowContextMenu}>
      <FullCell $width={nameColumnWidth} title={flowMeta.name}>
        {flowMeta.name}
      </FullCell>
      <Cell $width={initColumnWidth}>
        {initHumanized.humanizedNumber} {initHumanized.humanizedUnit}
      </Cell>
      <Cell $width={successColumnWidth}>
        <SuccessCellWrapper>
          <GaugeWrapper $dodEnabled={enableDod}>
            <GaugeContainer $dodEnabled={enableDod}>
              <Gauge settings={flowSettings} value={successPctData} />
              {flowSettings.timeout && (
                <TimeoutCaption>
                  {`Within ${flowSettings.timeout} ${unitMapping[flowSettings.timeout_unit]}`}
                </TimeoutCaption>
              )}
            </GaugeContainer>
            <div>
              {enableDod &&
                (dodQuery.isFetching || dodQuery.queued ? (
                  <DodNeutral>
                    <LoadingOutlined spin />
                  </DodNeutral>
                ) : successPctChange !== null ? (
                  <PercentBadge $positive={successPctChange > 0}>
                    {successPctChange > 0 ? <RiseOutlined /> : <FallOutlined />}
                    {`${Math.abs(successPctChange).toFixed(1)}%`}
                  </PercentBadge>
                ) : (
                  <DodNeutral>--</DodNeutral>
                ))}
            </div>
          </GaugeWrapper>
          <SuccessTrendWrapper>
            <Trend
              metricKey={successPctMetricKey}
              data={get(data, ['data', successPctMetricKey], [])}
              dodData={dodSuccessPctTimeseries}
              style={{ height: '40px' }}
            />
          </SuccessTrendWrapper>
        </SuccessCellWrapper>
      </Cell>
    </Row>
  );
};

export default RowData;
